/* ***************************************************************** */
/*                                                                   */
/* Licensed Materials - Property of Merative                         */
/*                                                                   */
/* (C) Copyright Merative Corp. 2025                                 */
/*                                                                   */
/* ***************************************************************** */
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {ToastNotification} from "@carbon/react";
import IVPickList from "mdx-ivcomp-common-ui/dist/src/components/ivpicklist/IVPickList";
import {useDispatch, useSelector, useStore,} from 'react-redux';
import {
    DATA_ARRAY_INDEX_INDEX,
    makeSelectItemAction,
    makeSetSearchTextAction,
} from "mdx-ivcomp-common-ui/dist/src/components/ivpicklist/iv-picklist-redux/iv-picklist-redux";
import {MORE_THAN_THREE_MESSAGE, MORE_THAN_THREE_TITLE,} from "../EnteralFormulasSearchPage";
import {loadingState, makeFormulaLoadActionThunk,} from "../redux/formula_search_redux";
import {useHistory} from "react-router-dom";
import {isClickInsideIVPickList} from '../../../../utils';
import {FORMULA_SEARCH_PANEL_NAME} from "../../../../common/constants";
import {compareStartsWithOrContains} from "../../../../../../utils/sort-search-utils";

const EnteralFormulaPicklist = ({
                                    formulaTypeName,
                                    picklistId,
                                    shouldFetchData = true,
                                }) => {
    const redux_store = useStore();
    const dispatch = useDispatch();
    const history = useHistory();
    const login_page = useSelector((state) => state.login_page);

    // these state variables will cause rerender
    const [moreThanThreeSelected, setMoreThanThreeSelected] = useState(false);
    const picklistRef = useRef(null);
    const picklistState = useSelector((state) => state[FORMULA_SEARCH_PANEL_NAME][picklistId]);

    useEffect(() => {
        if (shouldFetchData && picklistState?.data_array.length === 0) {
            dispatch(makeFormulaLoadActionThunk(picklistId, history, login_page, loadingState));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!isClickInsideIVPickList(event, picklistRef)) {
                dispatch(makeSetSearchTextAction(
                    "",
                    picklistId,
                    FORMULA_SEARCH_PANEL_NAME
                ));

                picklistRef.current?.setState({ updatedInputValue: "" });
            }
        }

        document.addEventListener('mousedown', handleClickOutside, true);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside, true);
        }
    }, [dispatch, picklistId, picklistRef]);

    const focusOnInputField = useCallback(() => {
        const drugInputFieldRef = picklistRef?.current?.drugInputFieldRef;
        drugInputFieldRef?.current?.focus();
    },[picklistRef]);

    const onPicklistSearchTextChange = useCallback( (event) => {
        dispatch(
            makeSetSearchTextAction(
                event.target.value,
                picklistId,
                FORMULA_SEARCH_PANEL_NAME
            )
        );
    },[dispatch,picklistId]);

    const onDismissMoreThanThreeMessage = useCallback( () => {
        setMoreThanThreeSelected(false);
        focusOnInputField();
    },[setMoreThanThreeSelected,focusOnInputField]);

    const onHandlePicklistSelection = useCallback( (item_index, event) => {
        const redux_state = redux_store.getState();
        const local_picklist_state = redux_state[FORMULA_SEARCH_PANEL_NAME][picklistId];
        const clicked_item = local_picklist_state.picklist_display_array[item_index];
        const selected_items = local_picklist_state.selected_items;
        const selected_items_count = selected_items.length;
        // does the clicked item already exist in the selected items?
        const item_already_selected = selected_items.some(
            (item) => item[DATA_ARRAY_INDEX_INDEX] === clicked_item[DATA_ARRAY_INDEX_INDEX]
        );
        if (selected_items_count>=3 && !item_already_selected) {
            // show more than 3 message
            setMoreThanThreeSelected(true);
            redux_store.dispatch(makeSetSearchTextAction("", picklistId, FORMULA_SEARCH_PANEL_NAME)); // close pulldown menu
        } else {
            // process the clicked item ...
            // duplicate IVPickList.js   validateAndAddItem(item_index) logic here
            redux_store.dispatch(makeSelectItemAction(item_index, picklistId, FORMULA_SEARCH_PANEL_NAME));
            redux_store.dispatch(makeSetSearchTextAction("", picklistId, FORMULA_SEARCH_PANEL_NAME)); // close pulldown menu
            focusOnInputField();
        }
    },[redux_store, picklistId, focusOnInputField]);

    const sortPicklistState = useCallback(() => {
        const mutablePicklistState = {...picklistState};
        mutablePicklistState.picklist_display_array = [...new Set(mutablePicklistState?.picklist_display_array)]
          .sort((a, b) => compareStartsWithOrContains(a[0], b[0], mutablePicklistState.search_text));

        return mutablePicklistState;
    }, [picklistState]);

    return (
        <>
            {moreThanThreeSelected && (
                <div className="formula-toast-message-wrapper">
                    <ToastNotification
                        autoFocus={true}
                        className="iv-comp-duplicate-drugs"
                        lowContrast={true}
                        title={MORE_THAN_THREE_TITLE}
                        caption=''
                        onCloseButtonClick={onDismissMoreThanThreeMessage}
                        subtitle={MORE_THAN_THREE_MESSAGE}
                        kind={"warning"}
                    />
                </div>
            )}
            <IVPickList
                ref={picklistRef}
                input_label="Formulas % - Select up to 3"
                input_label_postfix=""
                input_placeholder={`Find a ${formulaTypeName} Formula`}
                info_help_html={`<div>
                    <ul class="tooltip-ul">
                        <li>Click on the formula search field.</li>
                        <li>Enter the first few letters of a formula name.</li>
                        <li>Select the formula to display.</li>
                    </ul>
                </div>`}
                picklist_id={picklistId}
                page_name={FORMULA_SEARCH_PANEL_NAME}
                search_text_onchange={onPicklistSearchTextChange}
                iv_picklist_state={sortPicklistState()}
                redux_dispatch_function={dispatch}
                test_for_duplicates_flag={false}
                only_single_fetch_required={true}
                responseLoading={{}}
                pulldown_onselect={onHandlePicklistSelection}
            />
        </>
    );
};

export default EnteralFormulaPicklist;